import { useEffect, useState } from 'react';
import { classNames } from '~/utils/classNames';
import { BTN_GHOST, Select, Switch } from '~/components/workbench/tools/payments/ui';
import {
  inputToMoney,
  isoToLocalInput,
  isReference,
  localInputToIso,
  moneyToInput,
  parseJsonInput,
  parseNumberInput,
} from './field-model';
import { MAX_LIST_ITEMS, type FieldDefinition, type ImageValue } from './sdk';

// One input for every FieldType of @dunea/plugin-sdk primitives/fields, so
// custom content, block settings and automation inputs all edit values the
// same way. Controlled: `value` is the typed value (number, { url, alt },
// ISO string…), `onChange` gets the next typed value (undefined = unset).
// Lists (`field.list`) render one input per item with add/remove.

export interface ReferenceOption {
  value: string;
  label: string;
}

/** Loads the choices for a reference field (products, collections, entries of a type…). */
export type ReferenceLoader = (field: FieldDefinition) => Promise<ReferenceOption[]>;

export interface FieldInputProps {
  field: FieldDefinition;
  value: unknown;
  onChange: (next: unknown) => void;
  /** id of the (first) input, for <label htmlFor>. */
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Choices for reference fields; without it they take a pasted id. */
  references?: ReferenceLoader;
}

const INPUT = 'ui-input';

export function FieldInput(props: FieldInputProps) {
  if (props.field.list) {
    return <ListInput {...props} />;
  }

  return <SingleInput {...props} />;
}

function ListInput({ field, value, onChange, id, disabled, invalid, references }: FieldInputProps) {
  const items = Array.isArray(value) ? value : [];
  const item = { ...field, list: false, required: false };
  const set = (next: unknown[]) => onChange(next.length ? next : undefined);

  return (
    <div className="space-y-1.5">
      {items.map((v, i) => (
        <div key={i} className="flex items-start gap-1.5">
          <div className="min-w-0 flex-1">
            <SingleInput
              field={item}
              value={v}
              id={i === 0 ? id : undefined}
              disabled={disabled}
              invalid={invalid}
              references={references}
              onChange={(next) => set(items.map((old, j) => (j === i ? next : old)).filter((x) => x !== undefined))}
            />
          </div>
          <button
            type="button"
            className="ui-btn-icon mt-1 shrink-0"
            aria-label={`Remove item ${i + 1}`}
            disabled={disabled}
            onClick={() => set(items.filter((_, j) => j !== i))}
          >
            <span className="i-ph:x text-[13px] text-muted" />
          </button>
        </div>
      ))}
      {items.length < MAX_LIST_ITEMS && (
        <button
          type="button"
          className={BTN_GHOST}
          disabled={disabled}
          onClick={() => set([...items, field.type === 'boolean' ? false : ''])}
        >
          <span className="i-ph:plus text-[12px]" />
          Add {items.length ? 'another' : 'an item'}
        </button>
      )}
    </div>
  );
}

function SingleInput({ field, value, onChange, id, disabled, invalid, references }: FieldInputProps) {
  const aria = { id, 'aria-invalid': invalid || undefined, disabled };
  const text = typeof value === 'string' ? value : '';
  const setText = (s: string) => onChange(s === '' ? undefined : s);

  if (isReference(field.type)) {
    return (
      <ReferenceInput
        field={field}
        value={value}
        onChange={onChange}
        id={id}
        disabled={disabled}
        references={references}
      />
    );
  }

  switch (field.type) {
    case 'single_line_text':
      return <input {...aria} className={INPUT} value={text} onChange={(e) => setText(e.target.value)} />;
    case 'multi_line_text':
    case 'rich_text':
      return (
        <textarea
          {...aria}
          rows={field.type === 'rich_text' ? 5 : 3}
          className={classNames(INPUT, 'resize-y')}
          value={text}
          placeholder={field.type === 'rich_text' ? '**Bold**, _italic_, [links](https://…), lists' : undefined}
          onChange={(e) => setText(e.target.value)}
        />
      );
    case 'integer':
    case 'decimal':
      return (
        <input
          {...aria}
          type="number"
          inputMode={field.type === 'integer' ? 'numeric' : 'decimal'}
          step={field.type === 'integer' ? 1 : 'any'}
          min={field.validations?.min}
          max={field.validations?.max}
          className={classNames(INPUT, '!w-40')}
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(e) => onChange(parseNumberInput(e.target.value))}
        />
      );
    case 'boolean':
      return (
        <Switch
          checked={value === true}
          onChange={(next) => onChange(next)}
          label={value === true ? 'Yes' : 'No'}
          disabled={disabled}
        />
      );
    case 'date':
      return (
        <input
          {...aria}
          type="date"
          className={classNames(INPUT, '!w-48')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      );
    case 'date_time':
      return (
        <input
          {...aria}
          type="datetime-local"
          className={classNames(INPUT, '!w-60')}
          value={isoToLocalInput(value)}
          onChange={(e) => onChange(localInputToIso(e.target.value))}
        />
      );
    case 'url':
      return (
        <input
          {...aria}
          type="url"
          inputMode="url"
          placeholder="https://… or /page"
          className={INPUT}
          value={text}
          onChange={(e) => setText(e.target.value.trim())}
        />
      );
    case 'email':
      return (
        <input
          {...aria}
          type="email"
          inputMode="email"
          className={INPUT}
          value={text}
          onChange={(e) => setText(e.target.value.trim())}
        />
      );
    case 'page_reference':
      return (
        <input
          {...aria}
          placeholder="/about"
          className={classNames('ui-input-mono')}
          value={text}
          onChange={(e) => setText(e.target.value.trim())}
        />
      );
    case 'color':
      return <ColorInput value={text} onChange={setText} id={id} disabled={disabled} />;
    case 'select':
      return (
        <Select value={text} onChange={setText} disabled={disabled} label={field.label} className="max-w-xs">
          <option value="">{field.required ? 'Choose…' : 'None'}</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
    case 'money':
      return <MoneyInput value={value} onChange={onChange} id={id} disabled={disabled} invalid={invalid} />;
    case 'image':
      return <ImageInput value={value} onChange={onChange} id={id} disabled={disabled} invalid={invalid} />;
    case 'json':
      return <JsonInput value={value} onChange={onChange} id={id} disabled={disabled} />;
    default:
      return <input {...aria} className={INPUT} value={text} onChange={(e) => setText(e.target.value)} />;
  }
}

function ColorInput({
  value,
  onChange,
  id,
  disabled,
}: {
  value: string;
  onChange: (s: string) => void;
  id?: string;
  disabled?: boolean;
}) {
  const valid = /^#[0-9a-fA-F]{6}$/.test(value);

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label="Pick a color"
        disabled={disabled}
        value={valid ? value : '#000000'}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-8 w-10 shrink-0 cursor-pointer rounded-md border border-subtle bg-transparent p-0.5"
      />
      <input
        id={id}
        disabled={disabled}
        placeholder="#1A2B3C"
        className={classNames('ui-input-mono !w-32')}
        value={value}
        onChange={(e) => onChange(e.target.value.trim())}
      />
    </div>
  );
}

function MoneyInput({
  value,
  onChange,
  id,
  disabled,
  invalid,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const initial = moneyToInput(value);
  const [amount, setAmount] = useState(initial.amount);
  const [currency, setCurrency] = useState(initial.currency || 'USD');
  const [bad, setBad] = useState(false);

  // An external change (another entry loaded) resets the draft.
  const key = JSON.stringify(value ?? null);

  useEffect(() => {
    const next = moneyToInput(value);

    setAmount(next.amount);

    if (next.currency) {
      setCurrency(next.currency);
    }
    // Keyed on the serialized value, not the object identity.
  }, [key]);

  const commit = (a: string, c: string) => {
    const money = inputToMoney(a, c);

    setBad(money === null);

    if (money !== null) {
      onChange(money);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        inputMode="decimal"
        placeholder="0.00"
        aria-invalid={invalid || bad || undefined}
        disabled={disabled}
        className={classNames(INPUT, '!w-36')}
        value={amount}
        onChange={(e) => {
          setAmount(e.target.value);
          commit(e.target.value, currency);
        }}
      />
      <input
        aria-label="Currency"
        maxLength={3}
        disabled={disabled}
        className="ui-input-mono !w-20 uppercase"
        value={currency}
        onChange={(e) => {
          const c = e.target.value.toUpperCase();
          setCurrency(c);
          commit(amount, c);
        }}
      />
      {bad && <span className="text-[11px] text-danger">Enter an amount like 12.50 and a currency like USD</span>}
    </div>
  );
}

function ImageInput({
  value,
  onChange,
  id,
  disabled,
  invalid,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const img = (value && typeof value === 'object' ? value : {}) as Partial<ImageValue>;
  const url = img.url ?? '';
  const alt = img.alt ?? '';
  const set = (nextUrl: string, nextAlt: string) =>
    onChange(nextUrl ? (nextAlt ? { url: nextUrl, alt: nextAlt } : { url: nextUrl }) : undefined);
  const showPreview = /^(https:\/\/|\/)/.test(url);

  return (
    <div className="flex items-start gap-2.5">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-hairline bg-sunken">
        {showPreview ? (
          <img src={url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="i-ph:image text-[18px] text-faint" />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <input
          id={id}
          type="url"
          inputMode="url"
          placeholder="https://…/photo.jpg"
          aria-invalid={invalid || undefined}
          disabled={disabled}
          className={INPUT}
          value={url}
          onChange={(e) => set(e.target.value.trim(), alt)}
        />
        <input
          aria-label="Alt text"
          placeholder="Describe the image (alt text)"
          maxLength={300}
          disabled={disabled}
          className={INPUT}
          value={alt}
          onChange={(e) => set(url, e.target.value)}
        />
      </div>
    </div>
  );
}

function JsonInput({
  value,
  onChange,
  id,
  disabled,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  id?: string;
  disabled?: boolean;
}) {
  const [text, setText] = useState(() => (value === undefined ? '' : JSON.stringify(value, null, 2)));
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-1">
      <textarea
        id={id}
        rows={5}
        spellCheck={false}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
        className="ui-input-mono resize-y"
        value={text}
        onChange={(e) => {
          setText(e.target.value);

          const parsed = parseJsonInput(e.target.value);

          setError(parsed.ok ? null : parsed.error);

          if (parsed.ok) {
            onChange(parsed.value);
          }
        }}
      />
      {error && <span className="block text-[11px] text-danger">JSON {error}</span>}
    </div>
  );
}

function ReferenceInput({
  field,
  value,
  onChange,
  id,
  disabled,
  references,
}: {
  field: FieldDefinition;
  value: unknown;
  onChange: (next: unknown) => void;
  id?: string;
  disabled?: boolean;
  references?: ReferenceLoader;
}) {
  const [options, setOptions] = useState<ReferenceOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  const current = typeof value === 'string' ? value : '';
  const refKey = `${field.type}:${field.validations?.metaobjectType ?? ''}`;

  useEffect(() => {
    let cancelled = false;

    if (!references) {
      return undefined;
    }

    setOptions(null);
    setFailed(false);
    references(field)
      .then((list) => !cancelled && setOptions(list))
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
    };
    // Reload only when the reference target changes.
  }, [refKey, references]);

  if (!references || failed) {
    return (
      <input
        id={id}
        placeholder="Paste an id"
        disabled={disabled}
        className="ui-input-mono"
        value={current}
        onChange={(e) => onChange(e.target.value.trim() || undefined)}
      />
    );
  }

  if (!options) {
    return <div className="h-8 w-48 animate-pulse rounded-lg bg-sunken" aria-label="Loading choices" />;
  }

  const known = options.some((o) => o.value === current.toLowerCase());

  return (
    <Select
      value={current.toLowerCase()}
      onChange={(v) => onChange(v || undefined)}
      disabled={disabled}
      label={field.label}
      className="max-w-sm"
    >
      <option value="">{options.length ? 'None' : 'Nothing to choose yet'}</option>
      {current && !known && <option value={current.toLowerCase()}>Missing item ({current.slice(0, 8)}…)</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}
